import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Save, Send, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const DAYS = [
  { v: "mon", label: "Monday" }, { v: "tue", label: "Tuesday" },
  { v: "wed", label: "Wednesday" }, { v: "thu", label: "Thursday" },
  { v: "fri", label: "Friday" }, { v: "sat", label: "Saturday" },
  { v: "sun", label: "Sunday" },
];

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return iso.slice(0, 10);
  } catch { return iso; }
}

export function WeeklyDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [recipientsRaw, setRecipientsRaw] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/executive/digest/config");
      setCfg(r.data);
      setRecipientsRaw((r.data?.recipients || []).join(", "));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load digest config");
    }
  };
  useEffect(() => { load(); }, []);

  const parseRecipients = () => recipientsRaw
    .split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = parseRecipients();
      const invalid = recipients.filter((r) => !/^\S+@\S+\.\S+$/.test(r));
      if (invalid.length) {
        toast.error(`Invalid email(s): ${invalid.slice(0, 2).join(", ")}`);
        return;
      }
      const body = {
        enabled: cfg.enabled,
        day_of_week: cfg.day_of_week || "mon",
        hour_utc: Number(cfg.hour_utc ?? 7),
        minute_utc: Number(cfg.minute_utc ?? 0),
        recipients,
        note: cfg.note || "",
        custom_intro: cfg.custom_intro || null,
        sections: cfg.sections || {},
        attach_pl_pdf: !!cfg.attach_pl_pdf,
      };
      const r = await api.put("/executive/digest/config", body);
      toast.success("Digest schedule saved");
      setCfg(r.data.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testSend = async () => {
    const recipients = parseRecipients();
    if (!recipients.length) {
      toast.error("Add at least one recipient before test-send.");
      return;
    }
    setTesting(true);
    try {
      const r = await api.post("/executive/digest/test-send",
        { recipients, note: cfg.note || "" }, { timeout: 90000 });
      const d = r.data;
      if (d.status === "success") toast.success(`Test digest sent to ${d.ok_count} recipient(s)`);
      else if (d.status === "partial") toast.warning(`Sent to ${d.ok_count}, failed for ${d.fail_count}`);
      else toast.error(`Test send failed (${d.fail_count} failure(s))`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  if (!cfg) return <div className="h-40 shimmer rounded-lg" />;

  const lastRun = cfg.last_run_at;
  const lastStatus = cfg.last_run_status;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="digest-card">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-900">Weekly Board Digest</span>
        {cfg.enabled ? (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Scheduled</Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Off</Badge>
        )}
        {lastRun && (
          <span className="ml-auto text-[10px] text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Last run {fmtRel(lastRun)}
            {lastStatus && (
              lastStatus === "success"
                ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                : <AlertCircle className={`w-3 h-3 ${lastStatus === "partial" ? "text-amber-600" : "text-rose-600"}`} />
            )}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch id="digest-enabled" checked={cfg.enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="digest-enabled" />
          <Label htmlFor="digest-enabled" className="text-xs text-slate-700 cursor-pointer">
            Enable weekly digest
          </Label>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Day</Label>
          <Select value={cfg.day_of_week} onValueChange={(v) => setCfg({ ...cfg, day_of_week: v })}>
            <SelectTrigger className="h-9 bg-white" data-testid="digest-day"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Hour (UTC)</Label>
            <Input type="number" min={0} max={23} value={cfg.hour_utc}
              onChange={(e) => setCfg({ ...cfg, hour_utc: e.target.value })}
              data-testid="digest-hour" className="h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Min</Label>
            <Input type="number" min={0} max={59} value={cfg.minute_utc ?? 0}
              onChange={(e) => setCfg({ ...cfg, minute_utc: e.target.value })}
              data-testid="digest-min" className="h-9" />
          </div>
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Recipients (comma or newline separated)</Label>
        <Textarea rows={2} value={recipientsRaw}
          onChange={(e) => setRecipientsRaw(e.target.value)}
          placeholder="board@example.com, chair@example.com"
          data-testid="digest-recipients" />
      </div>

      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Note (optional)</Label>
        <Textarea rows={2} value={cfg.note || ""}
          onChange={(e) => setCfg({ ...cfg, note: e.target.value })}
          placeholder="Short message that appears at the top of every digest email…"
          data-testid="digest-note" />
      </div>

      {/* ---------- Customisation (added Feb 2026) ---------- */}
      <div className="mb-3 border-t border-slate-200 pt-3">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">
          Custom intro (optional — appears above the auto-generated sections)
        </Label>
        <Textarea rows={3} value={cfg.custom_intro || ""}
          onChange={(e) => setCfg({ ...cfg, custom_intro: e.target.value })}
          placeholder="e.g. Team — this week's focus is 30-day debtors and BU610 GM% recovery…"
          data-testid="digest-custom-intro" />
      </div>

      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
          Sections to include
        </Label>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {[
            ["overview_kpis",      "Financial KPIs (Rev/GP/OpEx/EBITDA/Cash)"],
            ["debtors",            "Debtors aging + top 5 90+"],
            ["operations",         "Operations (open/WIP/overdue jobs)"],
            ["hseq",               "HSEQ incidents (last 7 days)"],
            ["sustainability",     "Sustainability (fuel + CO2e FYTD)"],
            ["at_risk_clients",    "At-risk clients"],
            ["bu_performance",     "BU performance rollup"],
            ["contracts_expiring", "Contracts expiring (next 90 days)"],
            ["alerts",             "System alerts / exceptions"],
            ["trend_alerts",       "BU trend alerts (sales/GP drift)"],
            ["jsa_compliance",     "JSA compliance flags (techs < 90%)"],
            ["compliance_expiries", "Expiring licences / certs / inductions (30 days)"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.sections?.[key] !== false}
                onChange={(e) => setCfg({
                  ...cfg,
                  sections: { ...(cfg.sections || {}), [key]: e.target.checked },
                })}
                data-testid={`digest-section-${key}`}
                className="h-3.5 w-3.5"
              />
              <span className="text-slate-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.attach_pl_pdf}
            onChange={(e) => setCfg({ ...cfg, attach_pl_pdf: e.target.checked })}
            data-testid="digest-attach-pl-pdf"
            className="h-3.5 w-3.5"
          />
          <span className="text-slate-700">
            Also attach a branded P&L Summary PDF (current FY) for a printable board pack
          </span>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={saving} data-testid="digest-save">
          <Save className={`w-3.5 h-3.5 mr-1.5 ${saving ? "animate-pulse" : ""}`} />
          Save schedule
        </Button>
        <Button size="sm" variant="outline" onClick={testSend} disabled={testing} data-testid="digest-test">
          <Send className={`w-3.5 h-3.5 mr-1.5 ${testing ? "animate-pulse" : ""}`} />
          Send test now
        </Button>
        <span className="ml-auto text-[10px] text-slate-500">
          Times are UTC · Reuses the same PDF as Export PDF · Delivered via Resend
        </span>
      </div>
    </div>
  );
}
